import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { universalModalPaperProps, universalModalBackdropProps } from '../styles/modalStyles';

interface PrerequisiteRecord {
  Id: string;
  Name: string; // This is the Course Name from SF
  Course_Name__c: string;
  Prerequisite_Award__c: string;
  Can_Be_Expired__c: boolean;
  Validity_Period__c: number | null;
  Minimum_Age__c: number | null;
  Prerequisite_Order__c: number | null;
  Logic_Formula__c: string | null;
  Prerequisite_Group__c?: string;
  Group_Logic__c?: 'AND' | 'OR';
  Within_Group_Logic__c?: 'AND' | 'OR';
}

interface GroupedPrerequisites {
  [courseName: string]: PrerequisiteRecord[];
}

const PrerequisiteManagement: React.FC = () => {
  const { user } = useAuth();
  const [prerequisites, setPrerequisites] = useState<GroupedPrerequisites>({});
  const [availableAwards, setAvailableAwards] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [editingRecord, setEditingRecord] = useState<Partial<PrerequisiteRecord> | null>(null);

  const fetchPrerequisites = async () => {
    try {
      setLoading(true);
      setError(null);
      const [awardsResponse, prereqResponse] = await Promise.all([
        axios.get('/api/award_check_ajax'),
        axios.get<PrerequisiteRecord[]>('/api/prerequisites')
      ]);

      if (!awardsResponse.data?.available_awards) {
        throw new Error('Invalid awards data format');
      }
      if (!Array.isArray(prereqResponse.data)) {
        throw new Error('Invalid prerequisites data format');
      }

      const grouped: GroupedPrerequisites = {};
      for (const record of prereqResponse.data) {
          const courseName = record.Course_Name__c;
          if (!grouped[courseName]) {
              grouped[courseName] = [];
          }
          grouped[courseName].push(record);
      }

      setAvailableAwards(awardsResponse.data.available_awards);
      setPrerequisites(grouped);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrerequisites();
  }, []);

  const handleOpenDialog = (record?: PrerequisiteRecord, courseName?: string) => {
    setError(null);
    setSuccess(null);
    if (record) {
      setEditingRecord(record);
    } else {
              setEditingRecord({
        // Provide a full default object to satisfy the type
        Id: '',
        Name: courseName || '',
        Course_Name__c: courseName || '',
        Prerequisite_Award__c: '',
        Can_Be_Expired__c: false,
        Validity_Period__c: null,
        Minimum_Age__c: null,
        Prerequisite_Group__c: 'Group1',
        Group_Logic__c: 'AND',
        Within_Group_Logic__c: 'OR',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
  };
  
  const handleDialogChange = (field: keyof PrerequisiteRecord, value: any) => {
    if (editingRecord) {
      setEditingRecord({ ...editingRecord, [field]: value });
    }
  };
  
  const handleSave = async () => {
    if (!editingRecord) return;
    
    const recordToSave = {
        Name: editingRecord.Course_Name__c, // Set Name to be same as Course Name
        Course_Name__c: editingRecord.Course_Name__c,
        Prerequisite_Award__c: editingRecord.Prerequisite_Award__c || '', // Ensure it's not undefined
        Can_Be_Expired__c: editingRecord.Can_Be_Expired__c,
        Validity_Period__c: editingRecord.Validity_Period__c === null || editingRecord.Validity_Period__c === undefined ? 0 : editingRecord.Validity_Period__c,
        Minimum_Age__c: editingRecord.Minimum_Age__c === null || editingRecord.Minimum_Age__c === undefined ? 0 : editingRecord.Minimum_Age__c,
        Prerequisite_Group__c: editingRecord.Prerequisite_Group__c || 'Group1',
        Group_Logic__c: editingRecord.Group_Logic__c || 'AND',
        Within_Group_Logic__c: editingRecord.Within_Group_Logic__c || 'OR',
    };

    try {
      if (editingRecord.Id) {
        // Update existing record
        await axios.put(`/api/prerequisites/${editingRecord.Id}`, recordToSave);
        setSuccess('Prerequisite updated successfully.');
      } else {
        // Create new record
        await axios.post('/api/prerequisites', recordToSave);
        setSuccess('Prerequisite created successfully.');
      }
      handleCloseDialog();
      fetchPrerequisites(); // Refresh data
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save prerequisite.');
    }
  };
  
  const handleDelete = async (prereqId: string) => {
    if (window.confirm('Are you sure you want to delete this prerequisite rule?')) {
      try {
        await axios.delete(`/api/prerequisites/${prereqId}`);
        setSuccess('Prerequisite deleted successfully.');
        fetchPrerequisites(); // Refresh data
      } catch (err) {
        console.error('Delete failed:', err);
        setError('Failed to delete prerequisite.');
      }
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <Container>
        <Alert severity="error">You must be an admin to access this page.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">Prerequisite Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Prerequisite
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Course</TableCell>
                <TableCell>Prerequisites</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(prerequisites).map(([courseName, rules]) => (
                <TableRow key={courseName}>
                  <TableCell sx={{ verticalAlign: 'top', fontWeight: 'bold' }}>{courseName}</TableCell>
                  <TableCell>
                    {(() => {
                      // Group rules by prerequisite group
                      const groupedRules = rules.reduce((acc, rule) => {
                        const group = rule.Prerequisite_Group__c || 'Group1';
                        if (!acc[group]) acc[group] = [];
                        acc[group].push(rule);
                        return acc;
                      }, {} as { [key: string]: PrerequisiteRecord[] });

                      const groupEntries = Object.entries(groupedRules);
                      
                      return (
                        <Box>
                          {groupEntries.map(([groupName, groupRules], groupIndex) => (
                            <Box key={groupName} sx={{ mb: 2 }}>
                              {groupEntries.length > 1 && (
                                <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                  {groupName}:
                                </Typography>
                              )}
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                {groupRules.map((rule, ruleIndex) => (
                                  <Box key={rule.Id} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Chip
                                      label={`${rule.Prerequisite_Award__c || 'Age Only'}${rule.Validity_Period__c ? ` (${rule.Validity_Period__c}y)` : ''}${rule.Minimum_Age__c ? ` (age ${rule.Minimum_Age__c}+)` : ''}${rule.Can_Be_Expired__c ? ' (expired ok)' : ''}`}
                                      size="small"
                                      color={rule.Prerequisite_Group__c ? 'primary' : 'default'}
                                      variant="outlined"
                                      sx={{ mr: 0.5 }}
                                    />
                                    
                                    {ruleIndex < groupRules.length - 1 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5, fontWeight: 'bold' }}>
                                        {rule.Within_Group_Logic__c || 'OR'}
                                      </Typography>
                                    )}
                                    
                                    <Tooltip title="Edit">
                                       <IconButton size="small" onClick={() => handleOpenDialog(rule)}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton size="small" onClick={() => handleDelete(rule.Id)}><DeleteIcon color="error" fontSize="small" /></IconButton>
                                    </Tooltip>
                                  </Box>
                                ))}
                              </Box>
                              
                              {groupIndex < groupEntries.length - 1 && (
                                <Typography variant="body2" color="secondary" sx={{ textAlign: 'center', fontWeight: 'bold', my: 1 }}>
                                  {groupRules[0]?.Group_Logic__c || 'AND'}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                     <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog(undefined, courseName)}>Add</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        BackdropProps={universalModalBackdropProps}
        PaperProps={universalModalPaperProps}
      >
        <DialogTitle>{editingRecord?.Id ? 'Edit' : 'Add'} Prerequisite</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Course Name"
            fullWidth
            value={editingRecord?.Course_Name__c || ''}
            onChange={(e) => handleDialogChange('Course_Name__c', e.target.value)}
            required
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Prerequisite Award</InputLabel>
            <Select
              value={editingRecord?.Prerequisite_Award__c || ''}
              onChange={(e) => handleDialogChange('Prerequisite_Award__c', e.target.value)}
              label="Prerequisite Award"
            >
              <MenuItem value="">None (Age requirement only)</MenuItem>
              {availableAwards.map((award) => (
                <MenuItem key={award} value={award}>{award}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Validity Period (Years)"
            type="text"
            fullWidth
            value={editingRecord?.Validity_Period__c ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleDialogChange('Validity_Period__c', value === '' ? null : Number(value));
              }
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            helperText="Leave blank or 0 if no validity period."
          />
          <TextField
            margin="dense"
            label="Minimum Age Requirement"
            type="text"
            fullWidth
            value={editingRecord?.Minimum_Age__c ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleDialogChange('Minimum_Age__c', value === '' ? null : Number(value));
              }
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            helperText="Leave blank or 0 if no age requirement. Age will be calculated at course end date."
          />
          
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Logic Configuration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure how prerequisites are grouped and evaluated using AND/OR logic.
          </Typography>
          
          <TextField
            margin="dense"
            label="Prerequisite Group"
            fullWidth
            value={editingRecord?.Prerequisite_Group__c || ''}
            onChange={(e) => handleDialogChange('Prerequisite_Group__c', e.target.value)}
            helperText="Group identifier (e.g., Group1, Group2). Prerequisites in the same group are evaluated together."
            placeholder="Group1"
          />
          
          <FormControl fullWidth margin="dense">
            <InputLabel>Logic Between Groups</InputLabel>
            <Select
              value={editingRecord?.Group_Logic__c || 'AND'}
              onChange={(e) => handleDialogChange('Group_Logic__c', e.target.value as 'AND' | 'OR')}
              label="Logic Between Groups"
            >
              <MenuItem value="AND">AND - All groups must be satisfied</MenuItem>
              <MenuItem value="OR">OR - Any group can be satisfied</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
              How different prerequisite groups are combined
            </Typography>
          </FormControl>
          
          <FormControl fullWidth margin="dense">
            <InputLabel>Logic Within Group</InputLabel>
            <Select
              value={editingRecord?.Within_Group_Logic__c || 'OR'}
              onChange={(e) => handleDialogChange('Within_Group_Logic__c', e.target.value as 'AND' | 'OR')}
              label="Logic Within Group"
            >
              <MenuItem value="AND">AND - All prerequisites in group must be met</MenuItem>
              <MenuItem value="OR">OR - Any prerequisite in group can be met</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
              How prerequisites within the same group are evaluated
            </Typography>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={editingRecord?.Can_Be_Expired__c || false}
                onChange={(e) => handleDialogChange('Can_Be_Expired__c', e.target.checked)}
              />
            }
            label="Can Be Expired"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrerequisiteManagement; 